<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Banner, Button, FormControl, Input, Modal, Option, Select } from '@ui'
import type { SelectOption } from '@ui'
import type { Role } from '@/types/api'
import UserAvatar from './UserAvatar.vue'

/**
 * The one user form, in three shapes.
 *
 * - `create`: an admin adds an account. Everything is editable.
 * - `admin`:  an admin edits someone. Email is the account's identity and stays.
 * - `self`:   a user edits themselves, from the profile menu. Name, preferred
 *             name, function and picture are theirs to change; role is shown so the
 *             form still reads as "my account", but the admin owns it (US-01).
 *
 * `managedByElfieCare` locks the name in `admin` mode: it belongs to the
 * person's ElfieCare account (DECISIONS.md #12).
 *
 * The three share one component so the admin's edit and the user's edit can
 * never drift apart in layout or wording.
 */
export interface UserFormDraft {
  name: string
  /** What the product calls them. Empty means the full name is used. */
  preferredName: string
  email: string
  function: string
  role: Role
}

const props = withDefaults(
  defineProps<{
    open: boolean
    mode: 'create' | 'admin' | 'self'
    initial?: Partial<UserFormDraft>
    pending?: boolean
    /** Self mode only: the picture as the shell shows it, and whether a change to it is in flight. */
    avatarUrl?: string | null
    avatarPending?: boolean
    /** The person is linked to an ElfieCare account, which owns their name. */
    managedByElfieCare?: boolean
  }>(),
  {
    initial: () => ({}),
    pending: false,
    avatarUrl: null,
    avatarPending: false,
    managedByElfieCare: false,
  },
)

const emit = defineEmits<{
  close: []
  submit: [draft: UserFormDraft]
  /** Self mode: a picture was chosen, or removed. Applied at once, not on Save. */
  avatar: [file: File]
  removeAvatar: []
}>()

const onPicture = (event: Event): void => {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (file) emit('avatar', file)
}

const { t } = useI18n()

const roles: Role[] = ['user', 'manager', 'admin']

const blank = (): UserFormDraft => ({
  name: '',
  preferredName: '',
  email: '',
  function: '',
  role: 'user',
})

const draft = ref<UserFormDraft>({ ...blank(), ...props.initial })

// Re-seed on every open, so a cancelled edit does not leak into the next one.
watch(
  () => props.open,
  (open) => {
    if (open) draft.value = { ...blank(), ...props.initial }
  },
)

const isSelf = computed(() => props.mode === 'self')
const isCreate = computed(() => props.mode === 'create')

const title = computed(() => {
  if (isCreate.value) return t('users.add')
  return isSelf.value ? t('profile.editTitle') : t('users.editUser')
})

const roleOptions = computed<SelectOption[]>(() =>
  roles.map((role) => ({ label: t(`users.roles.${role}`), value: role })),
)
const roleValue = computed(() => roleOptions.value.find((o) => o.value === draft.value.role))
const setRole = (option: SelectOption | SelectOption[] | undefined): void => {
  const value = Array.isArray(option) ? option[0]?.value : option?.value
  if (value) draft.value.role = value as Role
}

const showSensitiveRoleWarning = computed(
  () => !isSelf.value && (draft.value.role === 'manager' || draft.value.role === 'admin'),
)

const canSubmit = computed(() => {
  if (draft.value.name.trim() === '') return false
  if (isSelf.value) return true
  return draft.value.email !== ''
})

const submit = (): void => {
  if (canSubmit.value) emit('submit', { ...draft.value })
}
</script>

<template>
  <Modal :model-value="open" :title="title" size="md" type="content" no-icon @close="emit('close')">
    <template #content>
      <div class="form" :data-mode="mode">
        <div v-if="isSelf" class="picture">
          <UserAvatar
            class="picture__preview"
            :name="draft.name"
            :avatar-url="avatarUrl"
            :size="64"
          />
          <div class="picture__text">
            <div class="picture__actions">
              <label class="picture__pick" :class="{ 'is-busy': avatarPending }">
                {{ avatarUrl ? t('profile.pictureReplace') : t('profile.pictureAdd') }}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  hidden
                  :disabled="avatarPending"
                  data-test="profile-picture"
                  @change="onPicture"
                />
              </label>
              <button
                v-if="avatarUrl"
                type="button"
                class="picture__remove"
                :disabled="avatarPending"
                @click="emit('removeAvatar')"
              >
                {{ t('profile.pictureRemove') }}
              </button>
            </div>
            <span class="picture__hint">{{ t('profile.pictureHint') }}</span>
          </div>
        </div>

        <FormControl
          name="user-name"
          :label="t('users.name')"
          :help-text="managedByElfieCare ? t('users.elfieCareManaged') : undefined"
          required
        >
          <Input v-model="draft.name" :disabled="managedByElfieCare" data-test="user-form-name" />
        </FormControl>

        <FormControl
          name="user-preferred-name"
          :label="t('users.preferredName')"
          :help-text="t('users.preferredNameHelp')"
        >
          <Input
            v-model="draft.preferredName"
            :maxlength="60"
            data-test="user-form-preferred-name"
          />
        </FormControl>

        <FormControl
          name="user-email"
          :label="t('users.email')"
          :help-text="isCreate ? undefined : t('users.emailImmutable')"
          required
        >
          <Input v-model="draft.email" :disabled="!isCreate" data-test="user-form-email" />
        </FormControl>

        <FormControl name="user-function" :label="t('users.function')">
          <Input v-model="draft.function" data-test="user-form-function" />
        </FormControl>

        <FormControl
          v-if="isSelf"
          :label="t('users.role')"
          :help-text="t('profile.roleManagedByAdmin')"
        >
          <Input
            :model-value="t(`users.roles.${draft.role}`)"
            disabled
            data-test="user-form-role"
          />
        </FormControl>
        <FormControl v-else :label="t('users.role')" required>
          <Select
            :model-value="roleValue"
            :options="roleOptions"
            data-test="user-form-role"
            @update:model-value="setRole"
          >
            <Option
              v-for="option in roleOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </Select>
        </FormControl>

        <Banner
          v-if="showSensitiveRoleWarning"
          variant="danger"
          :title="t('users.sensitiveRoleWarning')"
        />
      </div>
    </template>

    <template #actions>
      <Button type="secondary" @click="emit('close')">{{ t('common.cancel') }}</Button>
      <Button
        :loading="pending"
        :disabled="!canSubmit"
        :aria-disabled="!canSubmit"
        data-test="user-form-save"
        @click="submit"
      >
        {{ t('common.save') }}
      </Button>
    </template>
  </Modal>
</template>

<style scoped>
.form {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
}

.picture {
  display: flex;
  align-items: center;
  gap: var(--spacing-xl);
}

/* The avatar is drawn for the navy bar; on the form it needs the page's colours. */
.picture__preview {
  border-color: var(--line-subtle) !important;
  background: var(--surface-sunken) !important;
  color: var(--fg-secondary);
}

.picture__text {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.picture__actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
}

.picture__pick {
  padding: var(--spacing-xs) var(--spacing-lg);
  border: 1px solid var(--line-default);
  border-radius: var(--radius-full);
  font-size: var(--font-size-body-3);
  font-weight: var(--font-weight-semibold);
  color: var(--fg-secondary);
  cursor: pointer;
}

.picture__pick:hover {
  background: var(--surface-hover);
}

.picture__pick.is-busy {
  opacity: 0.5;
  cursor: progress;
}

.picture__remove {
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: var(--font-size-body-3);
  color: var(--fg-tertiary);
  text-decoration: underline;
  cursor: pointer;
}

.picture__hint {
  font-size: var(--font-size-caption);
  color: var(--fg-tertiary);
}
</style>
